import { useCallback, useEffect, useState } from "react";
import { useSession, useUser } from "@clerk/clerk-react";
import { Spinner, Select, SelectItem, Textarea } from "@heroui/react";
import {
  Phone,
  PhoneX,
  WhatsappLogo,
  Envelope,
  Wrench,
  CalendarX,
  NoteBlank,
  Plus,
  Archive,
} from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import ButtonNew from "components/buttonNew/buttonNew";
import {
  type CandidateFollowUpAction,
  CandidateFollowUpActionType,
  FOLLOW_UP_ACTION_TYPE_LABELS,
  FOLLOW_UP_ACTION_TYPE_COLORS,
} from "shared/types/candidate-follow-up";
import * as Sentry from "@sentry/react";

interface ActionLogTabProps {
  applicationId: number;
  onActionCountChange?: (count: number) => void;
}

const ACTION_TYPE_OPTIONS = [
  {
    key: CandidateFollowUpActionType.PHONE_REACHED,
    label: "Telefoon - bereikt",
    icon: Phone,
  },
  {
    key: CandidateFollowUpActionType.PHONE_UNREACHABLE,
    label: "Telefoon - onbereikbaar",
    icon: PhoneX,
  },
  {
    key: CandidateFollowUpActionType.WHATSAPP,
    label: "WhatsApp",
    icon: WhatsappLogo,
  },
  {
    key: CandidateFollowUpActionType.EMAIL,
    label: "E-mail",
    icon: Envelope,
  },
  {
    key: CandidateFollowUpActionType.TECHNICAL_PROBLEM,
    label: "Technisch probleem",
    icon: Wrench,
  },
  {
    key: CandidateFollowUpActionType.AGENDA_CONFLICT,
    label: "Agenda past niet",
    icon: CalendarX,
  },
  {
    key: CandidateFollowUpActionType.GENERAL_NOTE,
    label: "Algemene notitie",
    icon: NoteBlank,
  },
];

const ActionLogTab = ({
  applicationId,
  onActionCountChange,
}: ActionLogTabProps) => {
  const { session } = useSession();
  const { user } = useUser();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [actions, setActions] = useState<CandidateFollowUpAction[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);

  // Form state
  const [selectedType, setSelectedType] = useState<CandidateFollowUpActionType>(
    CandidateFollowUpActionType.PHONE_REACHED
  );
  const [remarks, setRemarks] = useState("");

  useEffect(() => {
    fetchActions();
  }, [applicationId]);

  const fetchActions = async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/follow-up/actions/${applicationId}`,
        { method: "GET" },
        session
      );

      if (!response.ok) throw new Error(`API error: ${response.status}`);

      const data = await response.json();
      setActions(data);
      onActionCountChange?.(data.length);
    } catch (error) {
      console.error("Error fetching follow-up actions:", error);
      Sentry.captureException(error, {
        tags: { feature: "candidateFollowUp", method: "fetchActions" },
        extra: { applicationId },
      });
      toast.error("Fout bij het ophalen van acties");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAction = useCallback(async () => {
    if (!user?.id) return;

    setIsSaving(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/rental-appointment/follow-up/actions`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            applicationId,
            actionType: selectedType,
            remarks: remarks || undefined,
            createdByUserId: user.id,
          }),
        },
        session
      );

      if (!response.ok) throw new Error("Failed to create action");

      const newAction = await response.json();
      setActions((prev) => [newAction, ...prev]);
      setShowAddForm(false);
      setRemarks("");
      setSelectedType(CandidateFollowUpActionType.PHONE_REACHED);
      toast.success("Actie toegevoegd");
      onActionCountChange?.(actions.length + 1);
    } catch (error) {
      console.error("Error creating follow-up action:", error);
      Sentry.captureException(error, {
        tags: { feature: "candidateFollowUp", method: "handleCreateAction" },
        extra: { applicationId, actionType: selectedType },
      });
      toast.error("Fout bij het aanmaken van actie");
    } finally {
      setIsSaving(false);
    }
  }, [applicationId, selectedType, remarks, session, user?.id, actions.length, onActionCountChange]);

  const handleArchiveAction = useCallback(
    async (actionId: number) => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental-appointment/follow-up/actions/${actionId}/archive`,
          { method: "PUT" },
          session
        );

        if (!response.ok) throw new Error("Failed to archive action");

        setActions((prev) => prev.filter((a) => a.id !== actionId));
        toast.success("Actie gearchiveerd");
        onActionCountChange?.(actions.length - 1);
      } catch (error) {
        console.error("Error archiving action:", error);
        Sentry.captureException(error, {
          tags: { feature: "candidateFollowUp", method: "handleArchiveAction" },
          extra: { actionId, applicationId },
        });
        toast.error("Fout bij het archiveren van actie");
      }
    },
    [session, actions.length, onActionCountChange]
  );

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("nl-BE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getActionIcon = (type: CandidateFollowUpActionType) => {
    const option = ACTION_TYPE_OPTIONS.find((o) => o.key === type);
    if (option) {
      const Icon = option.icon;
      return <Icon weight="duotone" size={16} />;
    }
    return <NoteBlank weight="duotone" size={16} />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner label="Acties ophalen..." color="success" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Add new action form */}
      {showAddForm ? (
        <div className="rounded-lg border bg-neutral-50 p-4">
          <h4 className="mb-3 text-sm font-medium text-neutral-700">
            Nieuwe actie toevoegen
          </h4>
          <div className="flex flex-col gap-3">
            <Select
              label="Type actie"
              selectedKeys={[selectedType]}
              onSelectionChange={(keys) => {
                const key = Array.from(keys)[0] as CandidateFollowUpActionType;
                if (key) setSelectedType(key);
              }}
              size="sm"
            >
              {ACTION_TYPE_OPTIONS.map((option) => (
                <SelectItem
                  key={option.key}
                  startContent={<option.icon size={16} />}
                >
                  {option.label}
                </SelectItem>
              ))}
            </Select>

            <Textarea
              label="Opmerkingen"
              placeholder="Voeg eventuele opmerkingen toe..."
              value={remarks}
              onValueChange={setRemarks}
              size="sm"
              minRows={2}
            />

            <div className="flex justify-end gap-2">
              <ButtonNew
                variant="secondaryNeutral"
                label="Annuleren"
                onClick={() => {
                  setShowAddForm(false);
                  setRemarks("");
                }}
              />
              <ButtonNew
                variant="primaryMoss"
                label={isSaving ? "Opslaan..." : "Opslaan"}
                onClick={handleCreateAction}
                isDisabled={isSaving}
              />
            </div>
          </div>
        </div>
      ) : (
        <ButtonNew
          variant="secondaryMoss"
          icon={<Plus size={14} />}
          label="Nieuwe actie"
          onClick={() => setShowAddForm(true)}
        />
      )}

      {/* Actions timeline */}
      <div className="flex flex-col gap-2">
        <h4 className="text-sm font-medium text-neutral-500">
          Geschiedenis ({actions.length}{" "}
          {actions.length === 1 ? "actie" : "acties"})
        </h4>

        {actions.length === 0 ? (
          <p className="py-4 text-center text-sm italic text-neutral-500">
            Nog geen opvolgingsacties geregistreerd
          </p>
        ) : (
          <div className="divide-y overflow-hidden rounded-lg border">
            {actions.map((action) => (
              <div
                key={action.id}
                className="flex items-start gap-3 p-3 hover:bg-neutral-50"
              >
                <div
                  className={`rounded-full p-2 ${FOLLOW_UP_ACTION_TYPE_COLORS[action.actionType]} bg-current/10`}
                  style={{
                    backgroundColor: `color-mix(in srgb, currentColor 10%, transparent)`,
                  }}
                >
                  {getActionIcon(action.actionType)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-neutral-700">
                      {FOLLOW_UP_ACTION_TYPE_LABELS[action.actionType]}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-neutral-500">
                    {formatDate(action.actionDate)}
                  </p>
                  {action.remarks && (
                    <p className="mt-1 text-sm text-neutral-600">
                      {action.remarks}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleArchiveAction(action.id)}
                  className="rounded-md p-1.5 text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600"
                  title="Archiveren"
                >
                  <Archive size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionLogTab;
